import { useState, useEffect } from 'react';
import PageHeader from '../components/PageHeader';
import { supabase } from '../lib/supabase';

const colors = {
  bg: '#f0f1f3',
  cardBg: '#ffffff',
  navy: '#071a52',
  greenDark: '#5cb83c',
  textPrimary: '#1a1a2e',
  textSecondary: '#6b7280',
  border: '#e2e4e8',
};

function Skipped() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showResolved, setShowResolved] = useState(false);
  const [resolving, setResolving] = useState(null);

  useEffect(() => {
    fetchSkipped();
  }, []);

  async function fetchSkipped() {
    setLoading(true);
    try {
      const { data } = await supabase
        .from('skipped_items')
        .select('*')
        .order('skipped_at', { ascending: false });
      if (data) setItems(data);
    } catch (err) {
      console.error('Error fetching skipped items:', err);
    }
    setLoading(false);
  }

  async function markResolved(id) {
    setResolving(id);
    try {
      await supabase
        .from('skipped_items')
        .update({ resolved: true })
        .eq('id', id);
      setItems(prev => prev.map(i => i.id === id ? { ...i, resolved: true } : i));
    } catch (err) {
      console.error('Error resolving item:', err);
    }
    setResolving(null);
  }

  const unresolved = items.filter(i => !i.resolved);
  const resolved = items.filter(i => i.resolved);
  const displayed = showResolved ? resolved : unresolved;

  function formatDate(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: colors.bg,
      padding: '20px',
      fontFamily: "'Outfit', sans-serif",
    }}>
      <PageHeader title="Skipped Items" icon="◎" />

      {/* Stat toggle cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '10px',
        marginBottom: '20px',
      }}>
        {[
          { label: 'Unresolved', count: unresolved.length, active: !showResolved, accent: '#d97706', onClick: () => setShowResolved(false) },
          { label: 'Resolved', count: resolved.length, active: showResolved, accent: colors.greenDark, onClick: () => setShowResolved(true) },
        ].map((s, i) => (
          <div
            key={i}
            onClick={s.onClick}
            style={{
              background: s.active ? colors.navy : colors.cardBg,
              border: `1px solid ${s.active ? colors.navy : colors.border}`,
              borderBottom: `3px solid ${s.active ? s.accent : s.accent}`,
              borderRadius: '6px',
              padding: '16px',
              textAlign: 'center',
              cursor: 'pointer',
            }}
          >
            <div style={{
              fontSize: '1.8rem',
              fontWeight: 800,
              color: s.active ? '#ffffff' : s.accent,
            }}>{s.count}</div>
            <div style={{
              fontSize: '0.68rem',
              color: s.active ? '#94a3b8' : colors.textSecondary,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              marginTop: '4px',
            }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Loading */}
      {loading && (
        <div style={{
          background: colors.cardBg,
          border: `1px solid ${colors.border}`,
          borderRadius: '6px',
          padding: '40px',
          textAlign: 'center',
          color: colors.textSecondary,
          fontSize: '0.9rem',
        }}>Loading skipped items...</div>
      )}

      {/* Empty state */}
      {!loading && displayed.length === 0 && (
        <div style={{
          background: colors.cardBg,
          border: `1px solid ${colors.border}`,
          borderRadius: '6px',
          padding: '40px',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: '2rem', marginBottom: '12px', color: colors.navy }}>
            {showResolved ? '—' : '✓'}
          </div>
          <div style={{ fontSize: '1.1rem', fontWeight: 700, color: colors.textPrimary, marginBottom: '8px' }}>
            {showResolved ? 'No resolved items yet' : 'No skipped items!'}
          </div>
          <div style={{ fontSize: '0.85rem', color: colors.textSecondary }}>
            {showResolved ? 'Resolved items will appear here' : 'All parts have been accounted for'}
          </div>
        </div>
      )}

      {/* Items list */}
      {!loading && displayed.map((item) => (
        <div key={item.id} style={{
          background: colors.cardBg,
          border: `1px solid ${colors.border}`,
          borderLeft: `3px solid ${item.resolved ? colors.greenDark : '#d97706'}`,
          borderRadius: '6px',
          padding: '14px 16px',
          marginBottom: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '14px',
        }}>
          <div style={{ flex: 1 }}>
            <div style={{
              fontSize: '0.92rem',
              fontWeight: 800,
              color: colors.textPrimary,
              marginBottom: '3px',
            }}>{item.part_no}</div>
            <div style={{ fontSize: '0.75rem', color: colors.textSecondary }}>
              {item.color_name} · Qty: {item.quantity}
            </div>
            <div style={{ fontSize: '0.7rem', color: colors.textSecondary, marginTop: '3px' }}>
              Bin: <strong>{item.bin}</strong> · Skipped {formatDate(item.skipped_at)}
            </div>
          </div>

          {item.resolved ? (
            <div style={{
              background: '#f0fdf4',
              border: '1px solid #bbf7d0',
              borderRadius: '4px',
              padding: '4px 10px',
              fontSize: '0.66rem',
              fontWeight: 700,
              color: '#16a34a',
              whiteSpace: 'nowrap',
            }}>
              ✓ Resolved
            </div>
          ) : (
            <button
              onClick={() => markResolved(item.id)}
              disabled={resolving === item.id}
              style={{
                padding: '7px 14px',
                background: resolving === item.id ? colors.border : colors.navy,
                color: '#ffffff',
                border: 'none',
                borderRadius: '6px',
                fontSize: '0.72rem',
                fontWeight: 700,
                fontFamily: "'Outfit', sans-serif",
                cursor: resolving === item.id ? 'default' : 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {resolving === item.id ? 'Saving...' : 'Mark Resolved'}
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

export default Skipped;